import { type DBSchema, type IDBPDatabase, openDB } from 'idb';

/**
 * Cached decrypted message data
 */
export interface DecryptedMessageCache {
  messageId: string;
  decryptedData: Record<string, unknown>;
  decryptedAt: Date;
}

/**
 * IndexedDB Schema for decrypted messages cache
 */
interface DecryptedMessagesDB extends DBSchema {
  decrypted_messages: {
    key: string; // messageId
    value: DecryptedMessageCache;
    indexes: {
      'by-decrypted-at': Date;
    };
  };
}

/**
 * Database class for caching decrypted Nostr messages
 * Messages are cached locally after decryption to avoid repeated decryption requests
 */
class DecryptedMessagesDatabase {
  private db: IDBPDatabase<DecryptedMessagesDB> | null = null;
  private readonly DB_NAME = 'formshive-decrypted-messages';
  private readonly DB_VERSION = 1;
  private initPromise: Promise<void> | null = null;

  async init(): Promise<void> {
    if (this.db) return;

    // Ensure we only initialize once
    if (this.initPromise) return this.initPromise;

    this.initPromise = this._init();
    return this.initPromise;
  }

  private async _init(): Promise<void> {
    try {
      // Check if IndexedDB is available
      if (!('indexedDB' in window)) {
        throw new Error('IndexedDB not supported');
      }

      this.db = await openDB<DecryptedMessagesDB>(this.DB_NAME, this.DB_VERSION, {
        upgrade(db) {
          // Create decrypted messages store
          if (!db.objectStoreNames.contains('decrypted_messages')) {
            const store = db.createObjectStore('decrypted_messages', {
              keyPath: 'messageId',
            });
            store.createIndex('by-decrypted-at', 'decryptedAt');
          }
        },
        blocked() {
          console.warn('Decrypted messages database upgrade blocked by another tab');
        },
        blocking() {
          console.warn('Decrypted messages database upgrade is blocking another tab');
        },
      });
    } catch (error) {
      console.error('Failed to initialize decrypted messages database:', error);
      this.initPromise = null;
      throw new Error(
        `Database initialization failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }

  private async ensureDB(): Promise<IDBPDatabase<DecryptedMessagesDB>> {
    if (!this.db) {
      await this.init();
    }

    if (!this.db) {
      throw new Error('Database not available');
    }

    return this.db;
  }

  /**
   * Get a cached decrypted message by ID
   */
  async getDecryptedMessage(messageId: string): Promise<DecryptedMessageCache | undefined> {
    try {
      const db = await this.ensureDB();
      const cached = await db.get('decrypted_messages', messageId);
      if (cached) {
        return {
          ...cached,
          decryptedAt: new Date(cached.decryptedAt),
        };
      }
      return undefined;
    } catch (error) {
      console.error('Failed to get decrypted message:', error);
      return undefined;
    }
  }

  /**
   * Cache a decrypted message
   */
  async cacheDecryptedMessage(
    messageId: string,
    decryptedData: Record<string, unknown>
  ): Promise<void> {
    try {
      const db = await this.ensureDB();
      await db.put('decrypted_messages', {
        messageId,
        decryptedData,
        decryptedAt: new Date(),
      });
    } catch (error) {
      console.error('Failed to cache decrypted message:', error);
      // Don't throw - caching is non-critical
    }
  }

  /**
   * Check if a message is cached
   */
  async isCached(messageId: string): Promise<boolean> {
    try {
      const cached = await this.getDecryptedMessage(messageId);
      return cached !== undefined;
    } catch {
      return false;
    }
  }

  /**
   * Get multiple cached messages by IDs
   */
  async getDecryptedMessages(messageIds: string[]): Promise<Map<string, DecryptedMessageCache>> {
    const result = new Map<string, DecryptedMessageCache>();
    try {
      const db = await this.ensureDB();
      for (const messageId of messageIds) {
        const cached = await db.get('decrypted_messages', messageId);
        if (cached) {
          result.set(messageId, {
            ...cached,
            decryptedAt: new Date(cached.decryptedAt),
          });
        }
      }
    } catch (error) {
      console.error('Failed to get decrypted messages:', error);
    }
    return result;
  }

  /**
   * Delete a cached message
   */
  async deleteDecryptedMessage(messageId: string): Promise<void> {
    try {
      const db = await this.ensureDB();
      await db.delete('decrypted_messages', messageId);
    } catch (error) {
      console.error('Failed to delete decrypted message:', error);
    }
  }

  /**
   * Clear all cached decrypted messages
   * Should be called on logout
   */
  async clearAll(): Promise<void> {
    try {
      const db = await this.ensureDB();
      await db.clear('decrypted_messages');
    } catch (error) {
      console.error('Failed to clear decrypted messages cache:', error);
    }
  }

  /**
   * Get the count of cached messages
   */
  async getCacheCount(): Promise<number> {
    try {
      const db = await this.ensureDB();
      return await db.count('decrypted_messages');
    } catch {
      return 0;
    }
  }
}

// Export singleton instance
export const decryptedMessagesDB = new DecryptedMessagesDatabase();
